import { MAP_ENGLISH_LEVEL_TO_SHORT, useCandidateUrl } from 'domain/shared/candidate';
import { SimpleLinkField } from 'framework/ViewTypes';
import type { FlatListTableProps, ListTableColumn } from 'framework/ui/ui-builder';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import type { SourcingCandidateRow } from '../types';
import LevelsField from './fields/LevelsField';
import BlackListBadge from 'domain/recruiting/candidate/shared/BlackListBadge';
import PotentialCandidateBadge from 'domain/recruiting/candidate/shared/PotentialCandidateBadge';
import ReferralCandidateBadge from 'domain/recruiting/candidate/shared/ReferralCandidateBadge';
import StudentBadge from 'domain/recruiting/candidate/shared/StudentBadge';
import { BadgeField } from 'framework/ui/field';

const getEnglishLevel = (level: string | null) => {
  if (!level) return EMPTY_VALUE_INDICATOR;

  return Object.entries(MAP_ENGLISH_LEVEL_TO_SHORT).find(([value]) => value === level)?.[1] ?? level;
};

const formatDate = (date: string | null) => {
  if (!date) return EMPTY_VALUE_INDICATOR;

  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime()) ? EMPTY_VALUE_INDICATOR : parsedDate.toLocaleDateString();
};

const getColumns = (getCandidateUrl: (id: string) => string): ListTableColumn<SourcingCandidateRow>[] => [
  {
    id: 'candidateName',
    label: 'Candidate name',
    width: 150,
    render: candidate => (
      <BadgeField
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
        badges={[
          <StudentBadge isVisible={!!candidate.isStudent} />,
          <BlackListBadge isVisible={!!candidate.isInBlackList} />,
          <PotentialCandidateBadge isVisible={!!candidate.isMarkedAsPotentialCandidate} />,
          <ReferralCandidateBadge isVisible={!!candidate.isReferral} />,
        ]}
      >
        <SimpleLinkField to={getCandidateUrl(candidate.id)} value={candidate.name.fullName} />
      </BadgeField>
    ),
  },
  {
    id: 'specialization',
    label: 'Specialization',
    width: 140,
    render: candidate => candidate.specialization ?? EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'technology',
    label: 'Technology',
    width: 125,
    render: candidate => candidate.technology ?? EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'level',
    label: 'Level',
    width: 80,
    render: candidate => <LevelsField level={candidate.level} isConfirmed={candidate.isLevelsConfirmed} />,
  },
  {
    id: 'englishLevel',
    label: 'English Level',
    width: 135,
    render: candidate => getEnglishLevel(candidate.englishLevel) || EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'location',
    label: 'Location',
    width: 110,
    render: candidate => candidate.residenceCountry ?? EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'skills',
    label: 'Skills',
    width: 200,
    render: candidate => candidate.skills.join(', ') || EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'lastAppliedJobPosting',
    label: 'Last applied',
    width: 130,
    render: candidate => candidate.lastAppliedVacancy ?? EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'lastStage',
    label: 'Last stage',
    width: 115,
    render: candidate => candidate.lastStage ?? EMPTY_VALUE_INDICATOR,
  },
  {
    id: 'lastInteraction',
    label: 'Last interaction',
    width: 160,
    render: candidate => formatDate(candidate.updatedAt),
  },
];

export const useSourcingCandidatesTableConfig = (
  rows: SourcingCandidateRow[],
): FlatListTableProps<SourcingCandidateRow> => {
  const { getSingleEntityUrl } = useCandidateUrl();

  return {
    rows,
    columns: getColumns(getSingleEntityUrl),
    emptyContent: 'No candidates match the selected sourcing criteria.',
  };
};
